import * as React from "react";

import { Component as List } from "@components/list/List";
import * as ListItem from "@components/list-item/index";
import { Skeleton } from "@components/skeleton/index";
import { DaperStory } from "@data/daper-client/types";
import type {
  PersonalizationPopularParams,
  PersonalizationStory,
} from "@data/personalization-client/types";
import { IntercomTracking } from "@interface/IntercomSlot";
import { IntercomComponentProps, IntercomInterface } from "@interface/types";
import { truthyFilter } from "@util/truthyFilter";
import { type AdRenderer } from "types/types";

import styles from "./styles.module.css";

export type Props = {
  intercom: IntercomInterface;
  personalizationPopularParams: PersonalizationPopularParams;
  numberOfRows?: number;
  adRenderers?: AdRenderer[];
  title?: string;
} & IntercomComponentProps;

const getPopularStoriesWithImagesForComponent = async (props: {
  intercom: IntercomInterface;
  personalizationPopularParams: PersonalizationPopularParams;
}) => {
  const { intercom, personalizationPopularParams } = props;

  const popularStories = (await intercom.getPopularResources(
    personalizationPopularParams
  )) as PersonalizationStory[];

  const responses = await Promise.allSettled(
    popularStories.map(async (popularStory) => {
      const storySearchResponse = (await intercom.getStories({
        id: popularStory.assetID,
        "attachments.image": true,
      })) as DaperStory[];

      if (!storySearchResponse.length) {
        throw Error(
          `No stories returned from getStories with id=${popularStory.assetID}`
        );
      }

      const story = storySearchResponse[0];

      if (!popularStory.thumbnailUrl) {
        const ledeImageId = story.extraMedia?.lede?.default.id;

        if (!ledeImageId) return null;

        const images = await intercom.getImages({ id: ledeImageId });

        return { ...story, images };
      }

      return { ...story, images: { baseUrl: popularStory.thumbnailUrl } };
    })
  );

  return responses
    .map((response) =>
      response && response.status === "fulfilled" ? response.value : null
    )
    .filter(truthyFilter);
};

export function PopularStoriesListItemList(props: Props) {
  const {
    intercom,
    personalizationPopularParams,
    numberOfRows = 5,
    adRenderers,
    title,
  } = props;

  const [componentPropsList, setComponentPropsList] = React.useState<
    ListItem.Props[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(true);
    getPopularStoriesWithImagesForComponent({
      intercom,
      personalizationPopularParams: {
        ...personalizationPopularParams,
        limit: numberOfRows,
      },
    }).then((stories) => {
      const propsList = stories.map((story) => ({
        image: {
          assetUrl: story.images.baseUrl,
          isLazy: true,
          aspectRatio: 3 / 2,
          mobileMaxWidth: 103,
          desktopMaxWidth: 141,
          linkToUrl: story.url,
        },
        text: story.headlines.web,
      }));

      setComponentPropsList(propsList);
      setIsLoading(false);
    });
  }, [personalizationPopularParams, numberOfRows]);

  const ListItemWithSkeleton = (componentProps: ListItem.Props) => (
    <Skeleton isLoading={isLoading}>
      <ListItem.WithImageOnLeft {...componentProps} />
    </Skeleton>
  );

  return (
    <IntercomTracking {...props} componentName="PopularStoriesListItemList">
      {title && <h3 className={styles.title}>{title}</h3>}
      <List
        IterableComponent={ListItemWithSkeleton}
        componentPropsList={componentPropsList}
        numberOfRows={numberOfRows}
        adRenderers={adRenderers}
      />
    </IntercomTracking>
  );
}
